import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable, Subject, switchMap, takeWhile, timer } from 'rxjs'
import { SessionSaveDto } from './dto/session-save-dto'
import { environment } from '../../../../environments/environment'
import { ProcessResultDto } from './dto/ProcessResultDto'
import { Scenario } from 'src/app/sungeo/model/zone-measures'
import { ParametreDto } from 'src/app/parametre/data-access/http/dto/parametre-dto'
import { ParametreTableauSaveDto } from 'src/app/parametre/data-access/http/dto/parametre-tableau-save-dto'
import { ParametreSpecfiqueBddEnedisDto } from 'src/app/parametre/data-access/http/dto/parametre-specfique-bdd-enedis-dto'
import { ids } from 'googleapis/build/src/apis/ids'
import { SessionSummaryDto } from './dto/session-summary-dto'
import { OAuthService } from 'angular-oauth2-oidc'

@Injectable({
  providedIn: 'root',
})
export class SunleonService {
  private overloadableParametres = new Subject<any>()
  private overloadableParametresTableau = new Subject<any>()
  private overloadableParametresSpécifique = new Subject<any>() 
  overloadableParametres$ = this.overloadableParametres.asObservable()
  overloadableParametresTableau$ = this.overloadableParametresTableau.asObservable()
  overloadableParametresSpécifique$ = this.overloadableParametresSpécifique.asObservable()

  private readonly pollingInterval = 5000 // Intervalle de vérification en millisecondes

  constructor(private http: HttpClient, private oAuthService: OAuthService) {}

  downloadCSV(sunleonSave: SessionSaveDto) {
    const path = `${environment.baseUrl}/sessions/csv`
    return this.http.post(path, sunleonSave, {
      observe: 'response',
      responseType: 'blob',
    })
  }

  transformSession(sunleonSave: SessionSaveDto) : Observable<ProcessResultDto> {
    const path = `${environment.baseUrl}/sessions/transformSession`
    return this.http.post<ProcessResultDto>(path, sunleonSave)
  }

  getPuissanceTotaleKwc(scenario: Scenario): Observable<number> {
    const path = `${environment.baseUrl}/sessions/puissanceTotaleKwc`
    return this.http.post<number>(path, scenario)
  }

  getProcessStatusSession(processId: string) {
    const path = `${environment.baseUrl}/sessions/transformSession/status/${processId}`
    return timer(0, 9000).pipe(
      // Interroge toutes les 9 secondes
      switchMap(() => this.http.get<ProcessResultDto>(path)),
      takeWhile((result) => result.status !== 'COMPLETED' && result.status !== 'FAILED', true) // Se termine une fois que le statut est COMPLETED ou FAILED
    )
  }

  checkSecondProcessStatus(processIdCreationCsv: string, processId_Evenement: string, idsimulation: number): Observable<ProcessResultDto> {
    const path = `${environment.baseUrl}/sessions/transformSession/status/csv/${processIdCreationCsv}/${processId_Evenement}/${idsimulation}`
    return timer(0, 9000).pipe( // Interroge toutes les 9 secondes
      switchMap(() => this.http.get<ProcessResultDto>(path)),
      takeWhile((result) => result.status !== 'COMPLETED' && result.status !== 'FAILED', true) // Se termine une fois que le statut est COMPLETED ou FAILED
    )
  }
    getScenarioById(id: number): Observable<Scenario> {
    const path = `${environment.baseUrl}/sessions/${id}/scenario`
    return this.http.get<Scenario>(path);
  }

  getLivrableAsCsv(idSimulation: number): Observable<any> {
    const path = `${environment.baseUrl}/sessions/upload-csv-livrable/${idSimulation}`
    return this.http.get(path, {
      observe: 'response', responseType: 'blob' as 'json'
    });
  }

  getGenerateurDevisAsCsv(idSimulation: number): Observable<any> {
    const path = `${environment.baseUrl}/sessions/upload-csv-livrable-devis/${idSimulation}`
    return this.http.get(path, {
      observe: 'response', responseType: 'blob' as 'json'
    });
  }

  getGenerateurValidationEngagementAsCsv(idSimulation: number): Observable<any> {
    const path = `${environment.baseUrl}/sessions/upload-csv-livrable-ve/${idSimulation}`
    return this.http.get(path, {
      observe: 'response', responseType: 'blob' as 'json'
    });
  }

  saveOverloadableParametres(code: string, value: string) {
    this.overloadableParametres.next({ code, value })
  }

  saveOverloadableParametresTableau(parametreDto: ParametreDto, valeurs: ParametreTableauSaveDto) {
    this.overloadableParametresTableau.next({ parametreDto, valeurs })
  }

  saveOverloadableParametresSpécifique(parametreDto: ParametreDto, valeurs: ParametreSpecfiqueBddEnedisDto) {
    this.overloadableParametresSpécifique.next({ parametreDto, valeurs })
  }

  generateLivrable(
    idSimulation: number,
    idFolder: string,
    csvPresentation: boolean,
    csvDevis: boolean,
    csvVE: boolean
  ): Observable<any> {

    const url = `${environment.generateurDocumentBaseUrl}/v1/documentsunleon/generate`;
    const sToken = `Bearer ${this.oAuthService.getIdToken()}`;

    const headers = new HttpHeaders({
      Authorization: sToken,
    });

    const documentTypes: string[] = [];
    if (csvDevis)        documentTypes.push('QUOTE');
    if (csvPresentation) documentTypes.push('SLIDES');
    if (csvVE)           documentTypes.push('VALIDATION');

    const payload = {
      projectId: idSimulation.toString(),
      folderId: idFolder,
      documentTypes,
    };

    return this.http.post(url, payload, { headers });
  }

  getSessionsSummaries(): Observable<SessionSummaryDto[]> {
    const path = `${environment.baseUrl}/sessions/summaries`;
    return this.http.get<SessionSummaryDto[]>(path);
  }

  getSessionEntree(id: number): Observable<SessionSaveDto> {
    const path = `${environment.baseUrl}/sessions/${id}/entree`;
    return this.http.get<SessionSaveDto>(path);
  }
}
